import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { UserService } from './user.service';
import { ClockRegisterClickRequest, ClockRegisterClickResponse, ClockRegisterStatus } from '../models/clock-register.model';
import { ClockRegisterTypeEnum } from '../enums/clock-register-type.enum';
import { PlatformService } from './platform.service';

@Injectable({
  providedIn: 'root',
})
export class ClockRegisterService {
  private initLoading: boolean = false;
  private readonly api = inject(ApiService);
  private readonly user = inject(UserService);
  private readonly platform = inject(PlatformService);

  private readonly clockActionInProgressSubject = new BehaviorSubject<boolean>(false);
  readonly clockActionInProgress$: Observable<boolean> = this.clockActionInProgressSubject.asObservable();

  // Schedule Time
  private statusSubject: BehaviorSubject<boolean> = new BehaviorSubject(null as any);
  public statusObservable: Observable<boolean> = this.statusSubject.asObservable();

  // Rest Time
  private restStatusSubject: BehaviorSubject<RestStatus> = new BehaviorSubject(null as any);
  public realStatusObservable: Observable<RestStatus> = this.restStatusSubject.asObservable();

  init() {
    if (!this.initLoading && this.user.getCurrentUser().role.slug === 'worker') {
      this.initLoading = true;
      this.api.myClockRegisterStatus().subscribe(
        (data: any) => {
          this.initLoading = false;
          this.statusSubject.next(data.status === ClockRegisterTypeEnum.IN);
          this.restStatusSubject.next(data.rest);
        },
        () => {
          this.initLoading = false;
        }
      );
    }
  }

  reset() {
    this.statusSubject.next(null as any);
  }

  click(options: Record<string, unknown> | null = null): Observable<ClockRegisterClickResponse> | null {
    if (this.clockActionInProgressSubject.value) {
      return null;
    }

    this.clockActionInProgressSubject.next(true);

    const body: ClockRegisterClickRequest = {
      ...options,
      is_mobile: this.platform.isMobile(),
    };

    return this.api.clickClockRegister(body).pipe(
      map(status => {
        if (status.status !== 'error') {
          this.statusSubject.next(status.status === ClockRegisterTypeEnum.IN);
          this.restStatusSubject.next(status.rest);
        }
        return status;
      }),
      catchError(error => throwError(() => error)),
      finalize(() => this.clockActionInProgressSubject.next(false))
    );
  }

  clickRest(body: any = null as any) {
    if (this.clockActionInProgressSubject.value) {
      return null;
    }
    this.clockActionInProgressSubject.next(true);
    return this.api.clickClockRestRegister(body).pipe(
      map(response => {
        if (response.status !== 'error') {
          this.restStatusSubject.next(response.rest_status);
        }
        return response;
      }),
      catchError(error => throwError(() => error)),
      finalize(() => this.clockActionInProgressSubject.next(false))
    );
  }

  onPusherClockEventReceived(data: ClockRegisterStatus): void {
    this.statusSubject.next(data.status === ClockRegisterTypeEnum.IN);
    this.restStatusSubject.next(data.rest);
  }
}

export interface RestStatus {
  rest_available: boolean; // et diu si poden fer o no
  rest_time_total_available_in_seconds: number; // el temps disponible. Null si no
  rest_time_consumed_in_seconds: number; // el temps consumit. Null si no
  rest_status_last_in: boolean; // si és IN l'últim status de rest. Null si no disponible
  rest_status_last_time: Date; // ultim status fitxat.
}
